import { Controller } from "@hotwired/stimulus"
import { get, patch } from "@rails/request.js"

// Personal "Congratulations, new badge!" corner card shown to the badge EARNER.
// - On connect: fetches the user's unacknowledged earned badges (login/day-start
//   catch-up) and renders ONE combined card in the lower-right.
// - Live: refreshes when the server pings a `badge_earned_personal` realtime
//   event (dispatched by RealtimeEventsChannel as `goat:realtime-event`).
// - X dismisses (acknowledges) ALL shown badges so they never re-show.
// - Clicking the card body opens the Badges screen.
// - Fully disabled when AppSetting.global_hide_badge_modals is on.
export default class extends Controller {
  static values = {
    unacknowledgedUrl: String,
    acknowledgeUrl: String,
    badgesUrl: String,
    disabled: Boolean
  }

  connect() {
    if (this.disabledValue) return

    this.shownKeys = []
    this.onRealtime = (event) => {
      if (event.detail && event.detail.type === "badge_earned_personal") this.refresh()
    }
    document.addEventListener("goat:realtime-event", this.onRealtime)
    this.refresh()
  }

  disconnect() {
    if (this.onRealtime) document.removeEventListener("goat:realtime-event", this.onRealtime)
    this.removeCard()
  }

  async refresh() {
    try {
      const response = await get(this.unacknowledgedUrlValue, { responseKind: "json" })
      if (!response.ok) return

      const data = await response.json
      this.render(data.badges || [])
    } catch (_error) {
      // Network blip — the next realtime ping or page load will retry.
    }
  }

  render(badges) {
    if (!badges.length) {
      this.removeCard()
      return
    }

    this.shownKeys = badges.map((badge) => badge.badge_key)
    if (!this.card) this.buildCard()

    const count = badges.length
    const heading = count === 1 ? "New Badge Earned" : `${count} New Badges Earned`
    const names = badges.map((badge) => this.escape(badge.label)).join(" · ")
    this.bodyEl.innerHTML =
      '<div class="gl-badge-card__eyebrow">🏆 Congratulations!</div>' +
      `<div class="gl-badge-card__title">${heading}</div>` +
      `<div class="gl-badge-card__names">${names}</div>` +
      '<div class="gl-badge-card__cta">View your badges →</div>'
  }

  buildCard() {
    this.card = document.createElement("div")
    this.card.className = "gl-badge-card"

    const close = document.createElement("button")
    close.type = "button"
    close.className = "gl-badge-card__close"
    close.setAttribute("aria-label", "Close")
    close.innerHTML = "&times;"
    close.addEventListener("click", (event) => {
      event.stopPropagation()
      this.dismiss()
    })

    this.bodyEl = document.createElement("div")
    this.bodyEl.className = "gl-badge-card__body"
    // role=button needs keyboard parity (WCAG 2.1.1): focusable + Enter/Space.
    this.bodyEl.setAttribute("role", "button")
    this.bodyEl.setAttribute("tabindex", "0")
    this.bodyEl.addEventListener("click", () => {
      window.location.href = this.badgesUrlValue
    })
    this.bodyEl.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        window.location.href = this.badgesUrlValue
      }
    })

    // Body first, close (X) last: in the default flex-row, DOM order is visual
    // order, so content sits left (18px padding) and the dismiss X sits right.
    this.card.append(this.bodyEl, close)
    this.element.appendChild(this.card)
    // Guard: the card may be dismissed (or the body Turbo-swapped) before the
    // next frame fires, leaving this.card null.
    requestAnimationFrame(() => this.card?.classList.add("is-visible"))
  }

  async dismiss() {
    const keys = this.shownKeys
    this.removeCard()
    if (!keys.length) return

    try {
      await patch(this.acknowledgeUrlValue, {
        body: JSON.stringify({ badge_keys: keys }),
        contentType: "application/json",
        responseKind: "json"
      })
    } catch (_error) {
      // If the ack fails it simply re-shows on next load — acceptable.
    }
  }

  removeCard() {
    if (this.card) this.card.remove()
    this.card = null
    this.bodyEl = null
  }

  escape(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : value
    return div.innerHTML
  }
}
